import { CompanySearchCondition } from 'src/entities/CompanySearchCondition.entity';
import { OrderSearchConditionEntity } from 'src/entities/OrderSearchCondition.entity';
import { SearchOperatorVo } from 'src/vo/SearchOperator.vo';

import { LEGACY_OPERATORS } from './legacyImport';
import { isLegacyPreset } from './serverPresets';

/** 一覧に出す取り込み元。会社共有はサーバ、ローカルは localStorage 由来 */
export type LegacyPresetSource =
  | { origin: 'company'; id: number; name: string; conditions: OrderSearchConditionEntity[] }
  | { origin: 'local'; name: string; conditions: OrderSearchConditionEntity[] };

/** 旧画面 (/plans) が検索条件を書き込む localStorage のキー。会社の区別を持たない。 */
export const LEGACY_LOCAL_STORAGE_KEY = 'saved-search-conditions';

const OPERATORS = new Set<string>(LEGACY_OPERATORS);

/**
 * localStorage の 1 条件を検証する。旧画面は形を検証せずに書き込むので、
 * 語彙外の logicalOperator / operator が入っていることがある。
 * ここで落とさないと、変換側が「この演算子は使えません」ではなく
 * 未知の値として扱ってしまい、利用者に理由が出ない。
 */
function toLegacyCondition(value: unknown): OrderSearchConditionEntity | null {
  if (typeof value !== 'object' || value === null) return null;
  const c = value as { logicalOperator?: unknown; key?: unknown; operator?: unknown; value?: unknown };
  if (c.logicalOperator !== 'and' && c.logicalOperator !== 'or') return null;
  if (typeof c.operator !== 'string' || !OPERATORS.has(c.operator)) return null;

  return {
    logicalOperator: c.logicalOperator,
    key: typeof c.key === 'string' ? c.key : undefined,
    operator: c.operator as SearchOperatorVo,
    value: typeof c.value === 'string' ? c.value : undefined,
  };
}

/** localStorage の 1 プリセットを検証する。壊れた値は黙って捨てる。 */
function toLocalSource(value: unknown): LegacyPresetSource | null {
  if (typeof value !== 'object' || value === null) return null;
  const preset = value as { name?: unknown; searchConditions?: unknown };
  if (typeof preset.name !== 'string' || preset.name === '') return null;
  if (!Array.isArray(preset.searchConditions)) return null;

  const conditions = preset.searchConditions
    .map(toLegacyCondition)
    .filter((c): c is OrderSearchConditionEntity => c !== null);
  if (conditions.length === 0) return null;

  return { origin: 'local', name: preset.name, conditions };
}

function readLocalSources(): LegacyPresetSource[] {
  const raw = localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY);
  if (!raw) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed.map(toLocalSource).filter((s): s is LegacyPresetSource => s !== null);
}

/**
 * 取り込み元の一覧を作る。どちらも取得済みの値なので追加リクエストは無い。
 *
 * 並びは「会社で共有した条件」→「このブラウザに保存した条件」。ダイアログの見出しと
 * 同じ順にしておき、表示側で並べ替えなくて済むようにする。
 *
 * localStorage 分は会社の区別を持たない（キーが 1 つで、旧画面も会社に関係なく読む）。
 * 別会社で作った条件が混ざりうるが、列が解決できない条件は変換時に unknownCustomField
 * などで表示されるため、そのまま出して利用者に選ばせる。
 */
export function collectLegacyPresetSources(
  companySearchConditions: CompanySearchCondition[] | undefined,
  companyId: string,
): LegacyPresetSource[] {
  const numericCompanyId = Number(companyId);

  const company: LegacyPresetSource[] = (companySearchConditions ?? [])
    .filter((it) => it.companyId === numericCompanyId && isLegacyPreset(it))
    .map((it) => ({ origin: 'company', id: it.id, name: it.name, conditions: it.searchConditions }));

  return [...company, ...readLocalSources()];
}
